import React, { createContext, useContext, useState, useEffect } from 'react';

const PermissionContext = createContext();

export const PermissionProvider = ({ children, token, API_URL, fetchWithAuth }) => {
    const [permissions, setPermissions] = useState({});
    const [userRole, setUserRole] = useState(null);
    const [userModuleOverrides, setUserModuleOverrides] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (token) {
            fetchUserPermissions();
        }
    }, [token]);

    const fetchUserPermissions = async () => {
        try {
            const res = await fetchWithAuth(`${API_URL}/user/permissions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            setPermissions(data.permissions || {});
            setUserRole(data.role || null);
            setUserModuleOverrides(data.user_module_overrides || {});
        } catch (err) {
            console.error('Error fetching permissions:', err);
        } finally {
            setLoading(false);
        }
    };

    const hasPermission = (module, action) => {
        // Administrator bypass - full access to everything
        if (userRole === 'Administrator') return true;

        if (!permissions[module]) return false;
        return permissions[module][action] === true;
    };

    const getModuleBranches = (module) => {
        // Returns additional branch IDs from user-specific module overrides
        if (!userModuleOverrides[module]) return [];
        return (userModuleOverrides[module].branches || []).map(b => b.branch_id);
    };

    const canView = (module) => hasPermission(module, 'read');
    const canCreate = (module) => hasPermission(module, 'create');
    const canUpdate = (module) => hasPermission(module, 'update');
    const canApprove = (module) => hasPermission(module, 'approve');
    const canDelete = (module) => hasPermission(module, 'delete');

    return (
        <PermissionContext.Provider value={{
            permissions,
            userRole,
            userModuleOverrides,
            loading,
            hasPermission,
            getModuleBranches,
            canView,
            canCreate,
            canUpdate,
            canApprove,
            canDelete
        }}>
            {children}
        </PermissionContext.Provider>
    );
};

export const usePermissions = () => {
    const context = useContext(PermissionContext);
    if (!context) {
        throw new Error('usePermissions must be used within PermissionProvider');
    }
    return context;
};
